import { ColDef, ValueFormatterParams } from "ag-grid-community";

const locale = navigator.language;

const dateFormatter = new Intl.DateTimeFormat(locale, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const dateTimeFormatter = new Intl.DateTimeFormat(locale, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const currencyFormatterCache = new Map<string, Intl.NumberFormat>();
// Currency codes that Intl.NumberFormat rejected — remembered so we don't re-throw
// once per row when a row carries a malformed cdCurrency.
const invalidCurrencyCodes = new Set<string>();

const getCurrencyFormatter = (
  currencyCode: string,
): Intl.NumberFormat | null => {
  if (invalidCurrencyCodes.has(currencyCode)) return null;
  let formatter = currencyFormatterCache.get(currencyCode);
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
      });
    } catch {
      // Invalid/unsupported ISO 4217 code (e.g. "US", "", "123") — Intl throws a
      // RangeError. Without this guard it bubbles up through the grid valueFormatter
      // or the detail CurrencyRenderer and crashes the render. Remember it and fall
      // back (see formatCurrency).
      invalidCurrencyCodes.add(currencyCode);
      return null;
    }
    currencyFormatterCache.set(currencyCode, formatter);
  }
  return formatter;
};

export interface ValueFormatterParamsEx<
  TData = any,
  TValue = any,
  TContext = any
> extends ValueFormatterParams<TData, TValue, TContext> {
  colDef: ColDef<TData, TValue> & {
    context?: {
      disableTimezoneConversion?: boolean;
    };
  };
}

export type ValueFormatterFn = (params: ValueFormatterParamsEx) => string;

export const valueFormatters: Record<string, ValueFormatterFn> = {
  date: (params) => {
    return formatDateTime(
      params.value,
      !params.colDef.context?.disableTimezoneConversion,
      true,
    );
  },

  datetime: (params) => {
    return formatDateTime(
      params.value,
      !params.colDef.context?.disableTimezoneConversion,
      false,
    );
  },

  currency: (params) => {
    return formatCurrency(params.value, params.data?.cdCurrency);
  },

  number: (params) => {
    return formatNumber(params.value) as string;
  },
};

export function formatNumber(n: unknown): unknown {
  if (typeof n === "number") return n.toLocaleString(locale);
  return n;
}

export function formatCurrency(value: unknown, currencyCode?: string): string {
  const n = Number(value);
  if (isNaN(n)) return String(value ?? "");
  const formatter = getCurrencyFormatter(currencyCode ?? "USD");
  if (!formatter) {
    // Unsupported currency code → safe fallback: the localized number, prefixed
    // with the raw code so the value is still legible (never throw inside a render).
    const num = n.toLocaleString(locale);
    return currencyCode ? `${currencyCode} ${num}` : num;
  }
  return formatter.format(n);
}

export function formatDateTime(
  value: unknown,
  convertTimezone: boolean,
  dateOnly: boolean,
) {
  let dt = value;
  if (!convertTimezone && typeof dt === "string") {
    dt = dt.replace("Z", "");
  }
  const d = new Date(dt as string);
  const formatter = dateOnly ? dateFormatter : dateTimeFormatter;
  return isNaN(d.getTime()) ? String(value ?? "") : formatter.format(d);
}
